// WI-09 re-scoped (owner's mockup docs/renders/new-sounds): "Today's new sounds", a short warm-up
// that stays a real question. The Learn home shows the next characters of the first script not yet
// met; the warm-up plays each one's sound and asks which character makes it (two to choose from
// for a gentle learner, four otherwise). Hearing it first IS the question here, so it gives
// nothing away (K2). Each is the character's own card: a right answer counts toward the goal and
// meets the card, exactly as in the script track.
import type { Pack } from '../../shared/pack/schema'
import { cardsOf } from './arcade/cards'
import { kanaCapsules } from './arcade/cardSets/kanaCapsules'
import type { Progress } from './arcade/progress'
import type { CardSetCtx, ChoiceItem } from './arcade/types'
import type { Rng } from './rng'
import { scriptDecks } from './kana'
import { t } from '../strings/en'

/** How many new sounds a warm-up brings in. */
export const NEW_SOUNDS = 3

export interface NewSound {
  cardId: string
  /** The character. */
  ch: string
  /** What it sounds like (the text the voice says). */
  say: string
}

/** Single characters of the first script deck, in teaching order. */
function characters(pack: Pack) {
  const deck = scriptDecks(pack)[0]
  if (!deck) return []
  return cardsOf(pack, deck).flatMap((c) => (c.kind === 'reading' && [...c.script].length === 1 ? [{ cardId: c.id, ch: c.script, say: c.say ?? c.script }] : []))
}

/** The next characters not yet met (none answered), in teaching order: today's new sounds. */
export function todaysNewSounds(pack: Pack, progress: Pick<Progress, 'cards'>, n = NEW_SOUNDS): NewSound[] {
  return characters(pack)
    .filter((c) => (progress.cards[c.cardId]?.box ?? 0) === 0)
    .slice(0, n)
}

/**
 * The warm-up: for each new sound, "Which character makes this sound?" with its sound to hear
 * and characters to pick from. The wrong ones are characters met already where there are enough,
 * else their neighbours in teaching order.
 */
export function warmupItems(ctx: CardSetCtx, sounds: readonly NewSound[], gentle: boolean, rand: Rng): ChoiceItem[] {
  const all = characters(ctx.pack)
  const met = all.filter((c) => (ctx.progress.cards[c.cardId]?.box ?? 0) > 0)
  const n = gentle ? 2 : 4
  return sounds.flatMap((s) => {
    const base = kanaCapsules.toItem(ctx, s.cardId, rand)
    if (!base) return []
    const i = all.findIndex((c) => c.cardId === s.cardId)
    const near = all.slice(Math.max(0, i - 4), i + 5)
    const pool = [...new Set([...met, ...near].map((c) => c.ch))].filter((ch) => ch !== s.ch)
    const wrong: string[] = []
    while (wrong.length < n - 1 && pool.length) wrong.push(pool.splice(Math.floor(rand() * pool.length), 1)[0]!)
    const options = [s.ch, ...wrong]
    for (let k = options.length - 1; k > 0; k--) {
      const j = Math.floor(rand() * (k + 1))
      ;[options[k], options[j]] = [options[j]!, options[k]!]
    }
    const item: ChoiceItem = {
      type: 'choice',
      cardId: s.cardId,
      listen: s.say,
      question: t.session.whichChar,
      options,
      answer: options.indexOf(s.ch),
      reveal: { ...base.reveal, big: s.ch },
      xp: base.xp,
      isNew: true,
      glyphs: true,
    }
    return [item]
  })
}
